import React, { useState } from 'react';
import { 
  X, LogOut, Bell, Shield, Lock, 
  Ghost, Globe, Users, ChevronLeft, ChevronRight, Check, AlertCircle 
} from 'lucide-react';
import { UserProfile } from '../../types';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  profile: UserProfile;
  onUpdate: (data: Partial<UserProfile>) => Promise<void>;
  onSignOut: () => void;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen, onClose, profile, onUpdate, onSignOut
}) => {
  // 'menu' | 'privacy' | 'privacy-confirm'
  const [currentView, setCurrentView] = useState<'menu' | 'privacy' | 'privacy-confirm'>('menu');
  const [pendingPrivacy, setPendingPrivacy] = useState<'public' | 'private' | 'ghost' | null>(null);
  const [loading, setLoading] = useState(false);

  // Helper to get current status
  const getCurrentLevel = () => {
      if (profile.isAnonymous) return 'ghost';
      if (profile.isPrivate) return 'private';
      return 'public';
  };

  // Step 1: Select Mode (Go to Confirm Screen)
  const selectPrivacyMode = (level: 'public' | 'private' | 'ghost') => {
      if (level === getCurrentLevel()) return; // No change
      setPendingPrivacy(level);
      setCurrentView('privacy-confirm');
  };

  // Step 2: Confirm Change (Update DB)
  const confirmPrivacyChange = async () => {
      if (!pendingPrivacy) return;
      setLoading(true);
      try {
          if (pendingPrivacy === 'public') {
              await onUpdate({ isPrivate: false, isAnonymous: false });
          } else if (pendingPrivacy === 'private') {
              await onUpdate({ isPrivate: true, isAnonymous: false });
          } else if (pendingPrivacy === 'ghost') {
              await onUpdate({ isPrivate: true, isAnonymous: true });
          }
          // Reset and go back
          setPendingPrivacy(null);
          setCurrentView('menu');
      } catch (error) {
          console.error("Failed to update privacy", error);
      }
      setLoading(false);
  };

  // Descriptions for the confirmation screen
  const privacyDetails = {
      public: {
          title: "Switch to Public",
          icon: Globe,
          desc: "Your profile will be visible to everyone on Scoop. Any user can follow you, view your closet, and send you messages. This is best for creators and social shoppers who want to build a following."
      },
      private: {
          title: "Switch to Private",
          icon: Lock,
          desc: "Your profile is locked. Only people you approve can follow you and see your activity. Existing followers will not be affected. Strangers cannot message you."
      },
      ghost: {
          title: "Enter Ghost Mode",
          icon: Ghost,
          desc: "You will be completely invisible. Your profile is hidden from search results, and no one can view your profile or DM you. This mode is strictly for shopping anonymously."
      }
  };

  if (!isOpen) return null;

  return (
    <div className="absolute inset-0 z-[150] bg-black flex flex-col animate-in slide-in-from-bottom duration-300">
      
      {/* Header */}
      <div className="p-6 border-b border-white/10 flex justify-between items-center bg-black">
        <div className="flex items-center gap-3">
            {currentView !== 'menu' && (
                <button 
                    onClick={() => {
                        if(currentView === 'privacy-confirm') setCurrentView('privacy');
                        else setCurrentView('menu');
                    }} 
                    className="text-white hover:opacity-70"
                >
                    <ChevronLeft size={24} />
                </button>
            )}
            <h2 className="text-2xl font-black text-white uppercase italic tracking-tighter">
                {currentView === 'menu' ? 'Settings' : 'Privacy'}
            </h2>
        </div>
        <button onClick={onClose} className="w-10 h-10 rounded-full bg-zinc-900 flex items-center justify-center text-white hover:bg-white/10">
            <X size={20} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-8">
        
        {/* --- MAIN MENU --- */}
        {currentView === 'menu' && (
            <>
                <button 
                    onClick={() => setCurrentView('privacy')}
                    className="w-full bg-zinc-900 p-5 rounded-2xl border border-white/10 flex items-center justify-between group hover:bg-zinc-800 transition-colors"
                >
                    <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            getCurrentLevel() === 'ghost' ? 'bg-white text-black' : 
                            getCurrentLevel() === 'private' ? 'bg-blue-600 text-white' : 'bg-zinc-800 text-white'
                        }`}>
                            {getCurrentLevel() === 'ghost' && <Ghost size={24} />}
                            {getCurrentLevel() === 'private' && <Lock size={24} />}
                            {getCurrentLevel() === 'public' && <Globe size={24} />}
                        </div>
                        <div className="text-left">
                            <h4 className="text-white font-bold capitalize">{getCurrentLevel()} Account</h4>
                            <p className="text-white/50 text-xs mt-1">
                                {getCurrentLevel() === 'ghost' ? 'Invisible to everyone.' : 
                                 getCurrentLevel() === 'private' ? 'Only friends can see you.' : 'Visible to everyone.'}
                            </p>
                        </div>
                    </div>
                    <ChevronRight size={20} className="text-white/30 group-hover:text-white transition-colors"/>
                </button>

                <div className="space-y-4">
                    <h3 className="text-xs font-bold text-white/40 uppercase tracking-widest">Account</h3>
                    <button className="w-full bg-zinc-900 p-4 rounded-xl flex items-center justify-between text-white border border-white/5">
                        <div className="flex items-center gap-3"><Bell size={20} className="text-white/60"/><span className="font-bold text-sm">Notifications</span></div>
                        <span className="text-white/20 text-xs">On</span>
                    </button>
                    <button className="w-full bg-zinc-900 p-4 rounded-xl flex items-center justify-between text-white border border-white/5">
                        <div className="flex items-center gap-3"><Shield size={20} className="text-white/60"/><span className="font-bold text-sm">Security</span></div>
                    </button>
                </div>

                <div className="pt-8 border-t border-white/10">
                    <button onClick={onSignOut} className="w-full py-4 bg-white text-black font-bold rounded-xl flex items-center justify-center gap-2 hover:bg-gray-200">
                        <LogOut size={18} /> Sign Out
                    </button>
                    <p className="text-center text-white/20 text-xs mt-4">Version 1.0.5 • Scoop</p>
                </div>
            </>
        )}

        {/* --- PRIVACY SELECTION --- */}
        {currentView === 'privacy' && (
            <div className="space-y-4 animate-in slide-in-from-right">
                <p className="text-white/50 text-sm mb-6">Choose how you want to appear to others on Scoop.</p>

                {/* Option: Public */}
                <button onClick={() => selectPrivacyMode('public')} className={`w-full p-5 rounded-2xl border flex items-center gap-4 text-left transition-all ${getCurrentLevel() === 'public' ? 'bg-white border-white' : 'bg-zinc-900 border-white/10 hover:bg-zinc-800'}`}>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 ${getCurrentLevel() === 'public' ? 'border-black' : 'border-white/30'}`}>
                        {getCurrentLevel() === 'public' && <div className="w-3 h-3 bg-black rounded-full" />}
                    </div>
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <Globe size={16} className={getCurrentLevel() === 'public' ? 'text-black' : 'text-white'} />
                            <h4 className={`font-black text-sm uppercase tracking-wider ${getCurrentLevel() === 'public' ? 'text-black' : 'text-white'}`}>Public</h4>
                        </div>
                    </div>
                </button>

                {/* Option: Private */}
                <button onClick={() => selectPrivacyMode('private')} className={`w-full p-5 rounded-2xl border flex items-center gap-4 text-left transition-all ${getCurrentLevel() === 'private' ? 'bg-white border-white' : 'bg-zinc-900 border-white/10 hover:bg-zinc-800'}`}>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 ${getCurrentLevel() === 'private' ? 'border-black' : 'border-white/30'}`}>
                        {getCurrentLevel() === 'private' && <div className="w-3 h-3 bg-black rounded-full" />}
                    </div>
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <Lock size={16} className={getCurrentLevel() === 'private' ? 'text-black' : 'text-white'} />
                            <h4 className={`font-black text-sm uppercase tracking-wider ${getCurrentLevel() === 'private' ? 'text-black' : 'text-white'}`}>Private</h4>
                        </div>
                    </div>
                </button>

                {/* Option: Ghost */}
                <button onClick={() => selectPrivacyMode('ghost')} className={`w-full p-5 rounded-2xl border flex items-center gap-4 text-left transition-all ${getCurrentLevel() === 'ghost' ? 'bg-white border-white' : 'bg-zinc-900 border-white/10 hover:bg-zinc-800'}`}>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 ${getCurrentLevel() === 'ghost' ? 'border-black' : 'border-white/30'}`}>
                        {getCurrentLevel() === 'ghost' && <div className="w-3 h-3 bg-black rounded-full" />}
                    </div>
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <Ghost size={16} className={getCurrentLevel() === 'ghost' ? 'text-black' : 'text-white'} />
                            <h4 className={`font-black text-sm uppercase tracking-wider ${getCurrentLevel() === 'ghost' ? 'text-black' : 'text-white'}`}>Ghost Mode</h4>
                        </div>
                    </div>
                </button>
            </div>
        )}

        {/* --- CONFIRMATION SCREEN --- */}
        {currentView === 'privacy-confirm' && pendingPrivacy && (
            <div className="flex flex-col h-full animate-in slide-in-from-right">
                <div className="flex-1 flex flex-col items-center justify-center text-center px-4">
                    <div className="w-20 h-20 bg-zinc-900 rounded-full flex items-center justify-center mb-6 border border-white/10">
                        {React.createElement(privacyDetails[pendingPrivacy].icon, { size: 40, className: "text-white" })}
                    </div>
                    
                    <h3 className="text-2xl font-black text-white mb-4 uppercase italic">
                        {privacyDetails[pendingPrivacy].title}
                    </h3>
                    
                    <p className="text-white/60 text-sm leading-relaxed max-w-xs mx-auto">
                        {privacyDetails[pendingPrivacy].desc}
                    </p>

                    <div className="mt-8 flex items-center gap-2 text-yellow-500 bg-yellow-500/10 px-4 py-2 rounded-lg">
                        <AlertCircle size={16} />
                        <span className="text-xs font-bold uppercase">This change is instant</span>
                    </div>
                </div>

                <div className="pt-6 border-t border-white/10">
                    <button 
                        onClick={confirmPrivacyChange} 
                        disabled={loading}
                        className="w-full py-4 bg-white text-black font-black text-sm uppercase tracking-widest rounded-xl mb-3 flex items-center justify-center gap-2 hover:bg-gray-200 transition-colors"
                    >
                        {loading ? 'Updating...' : 'Accept & Switch'}
                    </button>
                    <button 
                        onClick={() => setCurrentView('privacy')} 
                        disabled={loading}
                        className="w-full py-4 text-white/50 font-bold text-xs uppercase tracking-widest hover:text-white transition-colors"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        )}

      </div>
    </div>
  );
};